import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { Prompt, useHistory, useParams } from "react-router-dom";
import { NAVBAR_HEIGHT } from "components/navbar/NavbarAdmin";
import useSuperAdmin from "hooks/useSuperAdmin";
import AppTargetingMatrix from "views/admin/appTargeting/components/AppTargetingMatrix";
import {
  getOwnApps,
  getPartnerAppExclusions,
  getPartnerDetails,
  replacePartnerAppExclusions,
} from "views/admin/appTargeting/services";
import {
  ALL_PARTNER_APPS,
  ExclusionScope,
  OwnApp,
  PLATFORMS,
  PartnerAppExclusion,
  PartnerGame,
  cellKey,
} from "views/admin/appTargeting/types";

const UNSAVED_MESSAGE =
  "You have unsaved app targeting changes. Leave without saving?";

interface PendingAllGamesToggle {
  platformId: number;
  app: OwnApp;
  affectedGames: PartnerGame[];
  willHide: boolean;
}

const toHiddenSet = (rows: PartnerAppExclusion[]): Set<string> =>
  new Set(
    rows.map((row) => cellKey(row.platformId, row.partnerAppId, row.ownAppId))
  );

const AppTargetingDetail = () => {
  document.title = "Gamelight - App Targeting";

  const isSuperAdmin = useSuperAdmin();
  const history = useHistory();
  const toast = useToast();
  const { partnerId: partnerIdParam } = useParams<{ partnerId: string }>();
  const partnerId = Number(partnerIdParam);

  const [companyName, setCompanyName] = useState("");
  const [games, setGames] = useState<PartnerGame[]>([]);
  const [ownApps, setOwnApps] = useState<OwnApp[]>([]);
  const [initialHidden, setInitialHidden] = useState<Set<string>>(new Set());
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [pendingToggle, setPendingToggle] =
    useState<PendingAllGamesToggle | null>(null);
  const confirmDialog = useDisclosure();

  useEffect(() => {
    if (!partnerId) {
      setLoadError("Invalid partner id");
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    Promise.all([
      getPartnerDetails(partnerId),
      getOwnApps(),
      getPartnerAppExclusions(partnerId),
    ])
      .then(([details, apps, exclusions]) => {
        if (cancelled) return;
        setCompanyName(details.companyName);
        setGames(details.games);
        setOwnApps(apps);
        const loaded = toHiddenSet(exclusions);
        setInitialHidden(loaded);
        setHidden(new Set(loaded));
        setLoadError(null);
      })
      .catch((error) => {
        console.error("Error loading app targeting:", error);
        if (!cancelled) setLoadError("Failed to load the partner");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  const changeCount = useMemo(() => {
    let count = 0;
    hidden.forEach((key) => {
      if (!initialHidden.has(key)) count += 1;
    });
    initialHidden.forEach((key) => {
      if (!hidden.has(key)) count += 1;
    });
    return count;
  }, [hidden, initialHidden]);

  const isDirty = changeCount > 0;

  useEffect(() => {
    if (!isDirty) return;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = UNSAVED_MESSAGE;
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const flipCell = useCallback((key: string) => {
    setHidden((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const gamesOfPlatform = useCallback(
    (platformId: number, platformName: string) =>
      games.filter((game) => game.platform === platformName),
    [games]
  );

  const handleToggle = useCallback(
    (platformId: number, partnerAppId: string, ownAppId: string) => {
      const key = cellKey(platformId, partnerAppId, ownAppId);

      if (partnerAppId !== ALL_PARTNER_APPS) {
        flipCell(key);
        return;
      }

      // The All games cell affects the whole column. When every game in that column is
      // in its default (shown) state the flip is obvious and applies right away; when
      // some games carry their own rule the user should see what will happen first.
      const platformName =
        PLATFORMS.find((platform) => platform.id === platformId)?.name ?? "";
      const app = ownApps.find((ownApp) => ownApp.appId === ownAppId);
      const affectedGames = gamesOfPlatform(platformId, platformName).filter(
        (game) => hidden.has(cellKey(platformId, game.appId, ownAppId))
      );

      if (!app || affectedGames.length === 0) {
        flipCell(key);
        return;
      }

      setPendingToggle({
        platformId,
        app,
        affectedGames,
        willHide: !hidden.has(key),
      });
      confirmDialog.onOpen();
    },
    [flipCell, gamesOfPlatform, hidden, ownApps, confirmDialog]
  );

  const confirmPendingToggle = useCallback(() => {
    if (pendingToggle) {
      flipCell(
        cellKey(
          pendingToggle.platformId,
          ALL_PARTNER_APPS,
          pendingToggle.app.appId
        )
      );
    }
    setPendingToggle(null);
    confirmDialog.onClose();
  }, [pendingToggle, flipCell, confirmDialog]);

  const cancelPendingToggle = useCallback(() => {
    setPendingToggle(null);
    confirmDialog.onClose();
  }, [confirmDialog]);

  const handleDiscard = useCallback(() => {
    setHidden(new Set(initialHidden));
  }, [initialHidden]);

  const handleSave = useCallback(async () => {
    const scopes: ExclusionScope[] = [];

    PLATFORMS.forEach((platform) => {
      const apps = ownApps.filter((app) =>
        app.platformIds.includes(platform.id)
      );
      const partnerAppIds = [
        ALL_PARTNER_APPS,
        ...gamesOfPlatform(platform.id, platform.name).map(
          (game) => game.appId
        ),
      ];

      partnerAppIds.forEach((partnerAppId) => {
        const ownAppIds = apps
          .map((app) => app.appId)
          .filter((ownAppId) =>
            hidden.has(cellKey(platform.id, partnerAppId, ownAppId))
          );

        if (ownAppIds.length > 0) {
          scopes.push({ platformId: platform.id, partnerAppId, ownAppIds });
        }
      });
    });

    setIsSaving(true);
    try {
      const saved = await replacePartnerAppExclusions(partnerId, scopes);
      const savedHidden = toHiddenSet(saved);
      setInitialHidden(savedHidden);
      setHidden(new Set(savedHidden));

      toast({
        title: "Saved",
        description:
          savedHidden.size === 0
            ? `${companyName} is shown in every app`
            : `${savedHidden.size} hidden rule${
                savedHidden.size === 1 ? "" : "s"
              } for ${companyName}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving app targeting:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save app targeting",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [ownApps, gamesOfPlatform, hidden, partnerId, companyName, toast]);

  if (!isSuperAdmin) {
    return <></>;
  }

  return (
    <Box flex={1} minH={"90vh"} pt={NAVBAR_HEIGHT} px={4} pb={24}>
      <Prompt when={isDirty} message={UNSAVED_MESSAGE} />

      <Flex align="center" gap={3} mb={4} wrap="wrap">
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          size="sm"
          onClick={() => history.push("/app-targeting")}
        >
          Partners
        </Button>
        <Heading size="lg">{companyName || `Partner ${partnerId}`}</Heading>
        <Badge colorScheme="blue" variant="outline">
          {partnerId}
        </Badge>
      </Flex>

      <Text color="gray.500" fontSize="sm" mb={5}>
        Ticked means the partner's campaigns are shown in that app. Untick to
        hide. All games covers every game of the partner on that platform, games
        added later included. Web apps appear under both platforms because they
        serve both.
      </Text>

      {isLoading ? (
        <Flex align="center" gap={2}>
          <Spinner size="sm" />
          <Text fontSize="sm">Loading...</Text>
        </Flex>
      ) : loadError ? (
        <Text color="red.500">{loadError}</Text>
      ) : (
        PLATFORMS.map((platform) => (
          <AppTargetingMatrix
            key={platform.id}
            platformId={platform.id}
            platformName={platform.name}
            games={gamesOfPlatform(platform.id, platform.name)}
            ownApps={ownApps.filter((app) =>
              app.platformIds.includes(platform.id)
            )}
            hidden={hidden}
            initialHidden={initialHidden}
            onToggle={(partnerAppId, ownAppId) =>
              handleToggle(platform.id, partnerAppId, ownAppId)
            }
          />
        ))
      )}

      {!isLoading && !loadError && (
        <Flex
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          zIndex={10}
          justify="flex-end"
          align="center"
          gap={3}
          px={6}
          py={3}
          bg="white"
          borderTopWidth="1px"
          boxShadow="0 -4px 12px rgba(0,0,0,0.06)"
        >
          {isDirty ? (
            <Badge colorScheme="orange" variant="subtle">
              {changeCount} unsaved change{changeCount === 1 ? "" : "s"}
            </Badge>
          ) : (
            <Text fontSize="sm" color="gray.500">
              No unsaved changes
            </Text>
          )}
          <Button
            variant="outline"
            onClick={handleDiscard}
            isDisabled={!isDirty || isSaving}
          >
            Discard
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isDisabled={!isDirty}
            isLoading={isSaving}
            loadingText="Saving..."
          >
            Save
          </Button>
        </Flex>
      )}

      <Modal
        isOpen={confirmDialog.isOpen}
        onClose={cancelPendingToggle}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {pendingToggle?.willHide
              ? `Hide all games in ${pendingToggle?.app.name}?`
              : `Show all games in ${pendingToggle?.app.name}?`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {pendingToggle?.willHide ? (
              <Text mb={3}>
                This hides every game of the partner in{" "}
                <b>{pendingToggle.app.name}</b>, games added later included.
                These games already have their own hidden rule and stay hidden
                either way:
              </Text>
            ) : (
              <Text mb={3}>
                The partner-wide rule for <b>{pendingToggle?.app.name}</b> is
                removed. These games keep their own hidden rule and stay hidden
                until you untick them one by one:
              </Text>
            )}
            <Box as="ul" pl={5}>
              {pendingToggle?.affectedGames.map((game) => (
                <Text as="li" key={game.appId}>
                  {game.title}
                </Text>
              ))}
            </Box>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="outline" onClick={cancelPendingToggle}>
              Cancel
            </Button>
            <Button
              colorScheme={pendingToggle?.willHide ? "red" : "blue"}
              onClick={confirmPendingToggle}
            >
              {pendingToggle?.willHide ? "Hide all games" : "Show all games"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AppTargetingDetail;
